import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCoursesAPI, adminPackagesAPI, adminLessonsAPI } from '../services/adminApi';

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [courseFormData, setCourseFormData] = useState({
    package_id: '',
    title: '',
    description: '',
    order_position: 0,
    is_active: true
  });
  const [lessonFormData, setLessonFormData] = useState({
    course_id: '',
    title: '',
    description: '',
    video_id: '',
    cloudflare_stream_url: '',
    duration_minutes: 0,
    order_position: 0,
    is_active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, packagesRes] = await Promise.all([
        adminCoursesAPI.getAll(),
        adminPackagesAPI.getAll()
      ]);
      setCourses(coursesRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async (courseId) => {
    try {
      const response = await adminCoursesAPI.getLessons(courseId);
      setLessons(response.data);
      setSelectedCourse(courses.find(c => c.id === courseId));
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await adminCoursesAPI.update(editingCourse.id, courseFormData);
      } else {
        await adminCoursesAPI.create(courseFormData);
      }
      setShowCourseModal(false);
      setEditingCourse(null);
      setCourseFormData({ package_id: '', title: '', description: '', order_position: 0, is_active: true });
      loadData();
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Fehler beim Speichern!');
    }
  };

  const handleLessonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLesson) {
        await adminLessonsAPI.update(editingLesson.id, lessonFormData);
      } else {
        await adminLessonsAPI.create(lessonFormData);
      }
      setShowLessonModal(false);
      setEditingLesson(null);
      setLessonFormData({ course_id: '', title: '', description: '', video_id: '', cloudflare_stream_url: '', duration_minutes: 0, order_position: 0, is_active: true });
      if (selectedCourse) {
        loadLessons(selectedCourse.id);
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Fehler beim Speichern!');
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseFormData({
      package_id: course.package_id,
      title: course.title,
      description: course.description,
      order_position: course.order_position,
      is_active: course.is_active
    });
    setShowCourseModal(true);
  };

  const handleDeleteCourse = async (id) => {
    if (window.confirm('Wirklich löschen? Alle Lektionen werden auch gelöscht!')) {
      try {
        await adminCoursesAPI.delete(id);
        loadData();
        if (selectedCourse?.id === id) {
          setSelectedCourse(null);
          setLessons([]);
        }
      } catch (error) {
        console.error('Error deleting course:', error);
        alert('Fehler beim Löschen!');
      }
    }
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonFormData({
      course_id: lesson.course_id,
      title: lesson.title,
      description: lesson.description,
      video_id: lesson.video_id,
      cloudflare_stream_url: lesson.cloudflare_stream_url,
      duration_minutes: lesson.duration_minutes,
      order_position: lesson.order_position,
      is_active: lesson.is_active
    });
    setShowLessonModal(true);
  };

  const handleDeleteLesson = async (id) => {
    if (window.confirm('Wirklich löschen?')) {
      try {
        await adminLessonsAPI.delete(id);
        loadLessons(selectedCourse.id);
      } catch (error) {
        console.error('Error deleting lesson:', error);
        alert('Fehler beim Löschen!');
      }
    }
  };

  const openNewCourseModal = () => {
    setEditingCourse(null);
    setCourseFormData({ package_id: '', title: '', description: '', order_position: 0, is_active: true });
    setShowCourseModal(true);
  };

  const openNewLessonModal = () => {
    if (!selectedCourse) {
      alert('Bitte erst einen Kurs auswählen!');
      return;
    }
    setEditingLesson(null);
    setLessonFormData({ 
      course_id: selectedCourse.id, 
      title: '', 
      description: '', 
      video_id: '', 
      cloudflare_stream_url: '', 
      duration_minutes: 0, 
      order_position: 0, 
      is_active: true 
    });
    setShowLessonModal(true);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Lädt...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                👑 Admin Panel
              </h1>
              <nav className="flex space-x-4">
                <button onClick={() => navigate('/admin')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">Dashboard</button>
                <button onClick={() => navigate('/admin/packages')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">Pakete</button>
                <button onClick={() => navigate('/admin/courses')} className="px-4 py-2 text-primary-600 font-bold border-b-2 border-primary-600">Kurse</button>
                <button onClick={() => navigate('/admin/users')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">User</button>
              </nav>
            </div>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium">
              ← Zurück
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Courses Section */}
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Kurse</h2>
              <button
                onClick={openNewCourseModal}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 shadow-lg"
              >
                + Neuer Kurs
              </button>
            </div>

            <div className="space-y-4">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className={`bg-white rounded-xl shadow p-4 border-2 cursor-pointer transition-all ${
                    selectedCourse?.id === course.id ? 'border-primary-500' : 'border-gray-100 hover:border-primary-300'
                  }`}
                  onClick={() => loadLessons(course.id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{course.title}</h3>
                      <p className="text-sm text-gray-500">{course.package_name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${course.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {course.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{course.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">📹 {course.lesson_count} Lektionen</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditCourse(course); }}
                        className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteCourse(course.id); }}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lessons Section */}
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Lektionen {selectedCourse && `- ${selectedCourse.title}`}
              </h2>
              <button
                onClick={openNewLessonModal}
                disabled={!selectedCourse}
                className={`px-4 py-2 rounded-lg font-medium shadow-lg ${
                  selectedCourse
                    ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                + Neue Lektion
              </button>
            </div>

            {!selectedCourse ? (
              <div className="bg-white rounded-xl shadow p-12 text-center">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-gray-600">Wähle einen Kurs aus um Lektionen zu sehen</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lessons.length === 0 ? (
                  <div className="bg-white rounded-xl shadow p-8 text-center">
                    <p className="text-gray-600">Noch keine Lektionen vorhanden</p>
                  </div>
                ) : (
                  lessons.map((lesson) => (
                    <div key={lesson.id} className="bg-white rounded-xl shadow p-4 border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900">{lesson.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{lesson.description}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${lesson.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {lesson.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span>⏱️ {lesson.duration_minutes} Min.</span>
                        <span>🎥 {lesson.video_id || 'Kein Video'}</span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditLesson(lesson)}
                          className="flex-1 px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Course Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {editingCourse ? 'Kurs bearbeiten' : 'Neuer Kurs'}
            </h3>
            <form onSubmit={handleCourseSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paket</label>
                <select
                  value={courseFormData.package_id}
                  onChange={(e) => setCourseFormData({...courseFormData, package_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Paket wählen...</option>
                  {packages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={courseFormData.title}
                  onChange={(e) => setCourseFormData({...courseFormData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  value={courseFormData.description}
                  onChange={(e) => setCourseFormData({...courseFormData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reihenfolge</label>
                <input
                  type="number"
                  value={courseFormData.order_position}
                  onChange={(e) => setCourseFormData({...courseFormData, order_position: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="course_active"
                  checked={courseFormData.is_active}
                  onChange={(e) => setCourseFormData({...courseFormData, is_active: e.target.checked})}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="course_active" className="ml-2 text-sm font-medium text-gray-700">Aktiv</label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowCourseModal(false); setEditingCourse(null);}}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 my-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {editingLesson ? 'Lektion bearbeiten' : 'Neue Lektion'}
            </h3>
            <form onSubmit={handleLessonSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={lessonFormData.title}
                  onChange={(e) => setLessonFormData({...lessonFormData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  value={lessonFormData.description}
                  onChange={(e) => setLessonFormData({...lessonFormData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video ID</label>
                <input
                  type="text"
                  value={lessonFormData.video_id}
                  onChange={(e) => setLessonFormData({...lessonFormData, video_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="z.B. abc123def456"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cloudflare Stream URL</label>
                <input
                  type="text"
                  value={lessonFormData.cloudflare_stream_url}
                  onChange={(e) => setLessonFormData({...lessonFormData, cloudflare_stream_url: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="https://customer-..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dauer (Min.)</label>
                  <input
                    type="number"
                    value={lessonFormData.duration_minutes}
                    onChange={(e) => setLessonFormData({...lessonFormData, duration_minutes: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reihenfolge</label>
                  <input
                    type="number"
                    value={lessonFormData.order_position}
                    onChange={(e) => setLessonFormData({...lessonFormData, order_position: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="lesson_active"
                  checked={lessonFormData.is_active}
                  onChange={(e) => setLessonFormData({...lessonFormData, is_active: e.target.checked})}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="lesson_active" className="ml-2 text-sm font-medium text-gray-700">Aktiv</label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowLessonModal(false); setEditingLesson(null);}}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourses;
